import React, { useEffect, useRef, useState } from 'react';

const MIN_MS = 1400;

export default function ExplorerSplash({ progress = 0, phase = 'loading', onRetry }) {
  const started = useRef(Date.now());
  const [leaving, setLeaving] = useState(false);
  const [gone, setGone] = useState(false);
  const [bar, setBar] = useState(4);
  const targetRef = useRef(8);

  useEffect(() => {
    targetRef.current =
      phase === 'ready' ? 100 : phase === 'error' ? Math.max(bar, 12) : Math.max(8, Math.min(96, progress || 8));
  }, [progress, phase, bar]);

  useEffect(() => {
    const id = window.setInterval(() => {
      setBar((current) => {
        const target = targetRef.current;
        const next = current + (target - current) * 0.16;
        return Math.abs(next - target) < 0.35 ? target : next;
      });
    }, 40);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (phase !== 'ready' || leaving || gone) return undefined;
    const wait = Math.max(0, MIN_MS - (Date.now() - started.current));
    const start = window.setTimeout(() => setLeaving(true), wait);
    return () => window.clearTimeout(start);
  }, [phase, leaving, gone]);

  useEffect(() => {
    if (!leaving) return undefined;
    const end = window.setTimeout(() => setGone(true), 720);
    return () => window.clearTimeout(end);
  }, [leaving]);

  if (gone) return null;

  const label =
    phase === 'error'
      ? 'The landscape could not open'
      : phase === 'ready'
        ? 'Welcome in'
        : bar < 20
          ? 'Preparing the grounds'
          : bar < 70
            ? 'Opening the gardens'
            : 'Almost there';

  return (
    <div
      className={`be-splash${leaving ? ' is-leaving' : ''}`}
      role="status"
      aria-live="polite"
      aria-busy={phase === 'loading'}
    >
      <div className="be-splash-glow" aria-hidden="true" />
      <div className="be-splash-inner">
        <p className="be-splash-kicker">Belvoir Castle &amp; Gardens</p>
        <h1>A little world to explore</h1>
        <div className="be-splash-track">
          <span style={{ width: `${Math.round(bar)}%` }} />
        </div>
        <p className="be-splash-status">
          {phase === 'error' ? label : `${label}${phase === 'loading' ? ` · ${Math.round(bar)}%` : ''}`}
        </p>
        {phase === 'error' && (
          <button type="button" className="be-splash-retry" onClick={onRetry}>
            Try again
          </button>
        )}
      </div>
    </div>
  );
}
